import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { DynamicType, IDynamicInfoProps } from '@meitu/saas-editor-core/es/version-types';
import { createProgram, getShaderSource } from './utils';

const clearCanvas = (gl: WebGLRenderingContext, color = { r: 0, g: 0, b: 0, a: 0 }) => {
    gl.clearColor(color.r, color.g, color.b, color.a);
    gl.clear(gl.COLOR_BUFFER_BIT);
};

interface IProgramUniforms {
    startTime: number;
    pointerPosition: [number, number];
    scrollProgress: number;
    u_time: WebGLUniformLocation | null;
    u_ratio: WebGLUniformLocation | null;
    u_pointer_position: WebGLUniformLocation | null;
    u_scroll_progress: WebGLUniformLocation | null;
}

const ShaderDynamic: React.FC<{
    width: number;
    height: number;
    dynamicInfo?: IDynamicInfoProps;
}> = ({ width, height, dynamicInfo }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [gl, setGl] = useState<WebGLRenderingContext | null>(null);

    const [showBg, setShowBg] = useState(false);
    const currentRenderId = useRef(uuid());
    const programUniforms = useRef<IProgramUniforms>({
        startTime: Date.now(),
        pointerPosition: [0.5, 0.5],
        scrollProgress: 0,
        u_time: null,
        u_ratio: null,
        u_pointer_position: null,
        u_scroll_progress: null,
    });
    const [dynamicBgInfo, setDynamicBgInfo] = useState<IDynamicInfoProps>();
    const shaderUrlRef = useRef('');
    const bgElemRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (dynamicInfo && !dynamicInfo?.hidden && dynamicInfo?.dynamicType === DynamicType.Shader) {
            if (gl) {
                gl.viewport(0, 0, width, height);
            } else {
                setGl(canvasRef.current?.getContext('webgl') ?? null);
            }
            setShowBg(true);
            setDynamicBgInfo(dynamicInfo);
        } else {
            setShowBg(false);
            setDynamicBgInfo(undefined);
            if (gl) {
                currentRenderId.current = uuid();
                gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
                clearCanvas(gl);
            }
        }
    }, [dynamicInfo, gl, width, height]);

    useEffect(() => {
        if (!dynamicBgInfo || !bgElemRef.current || dynamicBgInfo?.dynamicType !== DynamicType.Shader) {
            return;
        }

        bgElemRef.current.style.backgroundColor = dynamicBgInfo?.shaderInfo?.backgroundColor || '';
    }, [dynamicBgInfo]);

    useEffect(() => {
        const renderId = uuid();
        let disposed = false;
        currentRenderId.current = renderId;

        const setupProgram = (gl: WebGLRenderingContext, vertexShaderSource: string, fragmentShaderSource: string) => {
            const program = createProgram(gl, vertexShaderSource, fragmentShaderSource);

            if (!program) {
                return;
            }

            gl.useProgram(program);
            // 获取属性和 uniform 变量位置
            programUniforms.current.u_time = gl.getUniformLocation(program, 'u_time');
            programUniforms.current.u_ratio = gl.getUniformLocation(program, 'u_ratio');
            programUniforms.current.u_pointer_position = gl.getUniformLocation(program, 'u_pointer_position');
            programUniforms.current.u_scroll_progress = gl.getUniformLocation(program, 'u_scroll_progress');

            const positionAttributeLocation = gl.getAttribLocation(program, 'a_position');
            const positionBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
            const positions = [-1.0, -1.0, 1.0, -1.0, -1.0, 1.0, 1.0, 1.0];
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

            gl.enableVertexAttribArray(positionAttributeLocation);
            gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);
        };

        const render = (gl: WebGLRenderingContext) => {
            const doAnimation = () => {
                if (disposed || currentRenderId.current !== renderId) {
                    return;
                }

                // 更新 uniforms
                const currentTime = Date.now();
                const elapsedTime =
                    (currentTime - programUniforms.current.startTime) *
                    ((dynamicBgInfo?.shaderInfo?.speed || 50) / 100 / 2);
                gl.uniform1f(programUniforms.current.u_time, elapsedTime);
                gl.uniform1f(programUniforms.current.u_ratio, width / height);
                gl.uniform2f(
                    programUniforms.current.u_pointer_position,
                    programUniforms.current.pointerPosition[0],
                    programUniforms.current.pointerPosition[1],
                );
                gl.uniform1f(programUniforms.current.u_scroll_progress, programUniforms.current.scrollProgress);
                // 清除画布
                gl.clear(gl.COLOR_BUFFER_BIT);
                // 绘制
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

                // 如果有编译错误，不进行渲染
                requestAnimationFrame(doAnimation);
            };

            requestAnimationFrame(doAnimation);
        };

        const run = async () => {
            if (!gl) {
                return;
            }
            if (
                dynamicBgInfo &&
                dynamicBgInfo.shaderInfo?.shaderUrl !== shaderUrlRef.current &&
                dynamicBgInfo.dynamicType === DynamicType.Shader
            ) {
                const url = dynamicBgInfo.shaderInfo?.shaderUrl || '';
                shaderUrlRef.current = url;
                if (!url) {
                    clearCanvas(gl);
                    return;
                }
                const data = await getShaderSource(url);
                if (disposed || currentRenderId.current !== renderId) {
                    return;
                }
                setupProgram(gl, data.vertexShader, data.fragmentShader);
                render(gl);
            } else {
                render(gl);
            }
        };

        void run();

        return () => {
            disposed = true;
            if (currentRenderId.current === renderId) {
                currentRenderId.current = uuid();
            }
        };
    }, [dynamicBgInfo, gl, height, width]);

    return (
        <div
            className="c-dynamic-bg-layer"
            ref={bgElemRef}
            style={{
                display: showBg ? 'block' : 'none',
            }}
        >
            <canvas
                ref={canvasRef}
                width={width}
                height={height}
                style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    left: '0',
                    right: '0',
                    top: '0',
                    bottom: '0',
                    overflow: 'hidden',
                }}
            />
        </div>
    );
};

export default ShaderDynamic;
